import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { fmtINR, fmtNum, fmtDateTime, API } from '@/lib/api';
import { MapView } from '@/components/MapView';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Loading, StatusBadge, PaymentBadge, PriorityBadge } from '@/components/Shared';
import { Truck, UserCheck, FileText, XCircle, Calendar, Download, MapPin, Package, CheckCircle2, CalendarClock, Flag, Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { logError } from '@/lib/log';

// Static map coordinates (demo positions until live GPS integration) hoisted from render.
const MAP_DRIVER_POS = { lat: 19.082, lng: 72.881 };
const MAP_ORIGIN_POS = { lat: 28.5563, lng: 77.3253 }; // depot: SRPS Filling Station, Sector 95, Noida

const SLOTS = [
  { label: '06:00 AM - 09:00 AM', h: 6 },
  { label: '09:00 AM - 12:00 PM', h: 9 },
  { label: '12:00 PM - 03:00 PM', h: 12 },
  { label: '03:00 PM - 09:00 PM', h: 15 },
  { label: '09:00 PM - 12:00 AM', h: 21 },
  { label: '12:00 AM - 06:00 AM', h: 0 },
];

export default function AdminOrderDetail() {
  const { orderId } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [invoice, setInvoice] = useState(null);
  const [openAssign, setOpenAssign] = useState(false);
  const [reDate, setReDate] = useState(() => new Date(Date.now() + 86400000).toISOString().slice(0, 10));
  const [reSlot, setReSlot] = useState('9');
  const [charge, setCharge] = useState('');
  const [aiDispatch, setAiDispatch] = useState(null);
  const [aiBusy, setAiBusy] = useState(false);

  const getAiDispatch = async () => {
    setAiBusy(true);
    try { const r = await api.get(`/ai/dispatch-suggest/${orderId}`); setAiDispatch(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || 'AI suggestion failed'); }
    setAiBusy(false);
  };

  const load = useCallback(async () => {
    const o = await api.get(`/orders/${orderId}`); setOrder(o.data); setCharge(String(o.data.pricing.delivery_charge));
    if (o.data.invoice_id) api.get(`/invoices/${o.data.invoice_id}`).then((r) => setInvoice(r.data)).catch((err) => logError('Failed to load invoice:', err));
  }, [orderId]);
  useEffect(() => { load(); }, [load]);
  const loadSuggestions = () => api.get(`/orders/${orderId}/dispatch-suggestions`).then((r) => setSuggestions(r.data));
  // Stable destination object so MapView doesn't re-render on every parent render.
  const mapDestination = useMemo(() => (order ? { lat: order.lat, lng: order.lng } : null), [order]);
  if (!order) return <Loading />;

  const assign = async (driverId) => {
    try { await api.post(`/orders/${orderId}/assign`, { driver_id: driverId }); toast.success('Driver assigned'); setOpenAssign(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const cancel = async () => { try { await api.post(`/orders/${orderId}/status`, { status: 'cancelled' }); toast.success('Order cancelled'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Cannot cancel'); } };
  const confirm = async () => { try { await api.post(`/orders/${orderId}/status`, { status: 'confirmed' }); toast.success('Confirmed'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const reschedule = async () => {
    try {
      const slot = SLOTS.find((s) => String(s.h) === reSlot) || SLOTS[1];
      const dt = new Date(`${reDate}T${String(slot.h).padStart(2, '0')}:00:00`);
      const label = `${dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })} · ${slot.label}`;
      const r = await api.post(`/orders/${orderId}/reschedule`, {
        delivery_time: label, schedule_type: 'scheduled', scheduled_at: dt.toISOString(),
        slot_date: reDate, slot_hour: slot.h,
      });
      toast.success(r.data?.whatsapp?.delivered ? 'Rescheduled — WhatsApp sent to customer' : 'Rescheduled — customer notified (WhatsApp pending Twilio setup)');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reschedule'); }
  };
  const updateCharge = async () => { try { await api.post(`/orders/${orderId}/charge`, { delivery_charge: Number(charge) }); toast.success('Charge updated'); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const updatePriority = async (p) => { try { await api.post(`/orders/${orderId}/priority`, { priority: p }); toast.success(`Priority set to ${p}`); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };
  const genInvoice = async () => { try { const r = await api.post(`/invoices/generate/${orderId}`); toast.success('Invoice generated'); setInvoice(r.data); load(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } };

  const canAssign = ['placed', 'confirmed', 'driver_assigned'].includes(order.status);

  return (
    <div className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div><h2 className="font-display text-xl font-semibold">{order.order_no}</h2><p className="text-sm text-muted-foreground">{order.customer_name} · {order.payment_method}</p></div>
        <div className="flex items-center gap-2"><PriorityBadge priority={order.priority} /><StatusBadge status={order.status} label={order.status_label} /></div>
      </div>

      <div className="flex flex-wrap gap-2">
        {order.status === 'placed' && <Button size="sm" onClick={confirm} data-testid="confirm-order-button"><CheckCircle2 className="h-4 w-4 mr-1" />Confirm</Button>}
        {canAssign && (
          <Dialog open={openAssign} onOpenChange={(v) => { setOpenAssign(v); if (v) loadSuggestions(); }}>
            <DialogTrigger asChild><Button size="sm" data-testid="assign-driver-button"><UserCheck className="h-4 w-4 mr-1" />{order.driver_id ? 'Reassign' : 'Assign'} Driver</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Dispatch — Nearest Drivers</DialogTitle></DialogHeader>
              <div className="flex items-center justify-between -mt-1">
                <p className="text-xs text-muted-foreground">Rule-based ranking below, or let AI weigh capacity, distance, rating & weather.</p>
                <Button size="sm" variant="secondary" onClick={getAiDispatch} disabled={aiBusy} data-testid="ai-dispatch-button">
                  {aiBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}AI Suggest
                </Button>
              </div>
              {aiDispatch && !aiBusy && (
                <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="ai-dispatch-result">
                  <p className="text-xs font-semibold flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-primary" />AI Recommendation</p>
                  {(aiDispatch.recommendations || []).slice(0, 3).map((r) => (
                    <div key={r.driver_id} className="flex items-center justify-between gap-2 text-sm">
                      <div>
                        <p className="font-medium">{r.name} <span className="text-xs text-muted-foreground">{r.vehicle_number} · ETA {r.eta_min}m · score {r.score}</span></p>
                        <p className="text-xs text-muted-foreground">{r.reason}</p>
                      </div>
                      <Button size="sm" onClick={() => assign(r.driver_id)} data-testid={`ai-assign-${r.driver_id}`}>Assign</Button>
                    </div>
                  ))}
                  {aiDispatch.weather_note && <p className="text-[11px] text-muted-foreground">{aiDispatch.weather_note}</p>}
                </div>
              )}
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {suggestions.map((s) => (
                  <div key={s.driver_id} className="rounded-lg border border-border p-3 flex items-center justify-between" data-testid={`dispatch-driver-${s.driver_id}`}>
                    <div><p className="font-medium text-sm">{s.name} <span className={`text-xs ${s.status === 'available' ? 'text-emerald-600' : 'text-amber-600'}`}>({s.status})</span></p><p className="text-xs text-muted-foreground">{s.vehicle_number} · {fmtNum(s.tank_capacity)}L · {s.distance_km}km · ETA {s.eta_min}m {!s.capacity_ok && '· ⚠ capacity'}</p></div>
                    <Button size="sm" onClick={() => assign(s.driver_id)}>Assign</Button>
                  </div>
                ))}
                {suggestions.length === 0 && <p className="text-sm text-muted-foreground">Loading drivers...</p>}
              </div>
            </DialogContent>
          </Dialog>
        )}
        {order.status === 'completed' && !invoice && <Button size="sm" onClick={genInvoice} data-testid="generate-invoice-button"><FileText className="h-4 w-4 mr-1" />Generate Invoice</Button>}
        {invoice && <Button size="sm" variant="secondary" onClick={() => window.open(`${API}/invoices/${invoice.id}/pdf`, '_blank')} data-testid="view-invoice-button"><Download className="h-4 w-4 mr-1" />Invoice PDF</Button>}
        {!['completed', 'invoice_generated', 'cancelled'].includes(order.status) && <Button size="sm" variant="destructive" onClick={cancel} data-testid="cancel-order-button"><XCircle className="h-4 w-4 mr-1" />Cancel</Button>}
        {!['completed', 'invoice_generated', 'cancelled'].includes(order.status) && (
          <div className="flex items-center gap-1.5 ml-auto">
            <Flag className="h-4 w-4 text-muted-foreground" />
            <Select value={order.priority || 'normal'} onValueChange={updatePriority}>
              <SelectTrigger className="h-9 w-36" data-testid="order-priority-select"><SelectValue /></SelectTrigger>
              <SelectContent>{['urgent', 'high', 'normal', 'low'].map((p) => <SelectItem key={p} value={p} className="capitalize">{p} priority</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border border-border bg-card p-4 space-y-2 text-sm">
          <p className="font-semibold">Order Details</p>
          <div className="flex items-start gap-2"><Package className="h-4 w-4 mt-0.5 text-primary" />{fmtNum(order.pricing.qty)} {order.pricing.unit} · {order.pricing.product_name}</div>
          <div className="flex items-start gap-2"><MapPin className="h-4 w-4 mt-0.5 text-primary" />{order.address_text}</div>
          <div className="flex items-start gap-2"><CalendarClock className="h-4 w-4 mt-0.5 text-primary" /><span>{order.schedule_type === 'scheduled' ? <>Scheduled: <span className="font-medium">{order.delivery_time}</span></> : <>Delivery: <span className="font-medium">ASAP (Deliver Now)</span></>}</span></div>
          {order.driver_name && <div className="flex items-center gap-2"><Truck className="h-4 w-4 text-primary" />{order.driver_name} · {order.vehicle_number}</div>}
          <div className="border-t border-border pt-2 space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Fuel Invoice (Nil GST for diesel)</p>
            <Row l="Fuel Subtotal" r={fmtINR(order.pricing.subtotal)} />
            {order.pricing.product_gst_pct > 0 && <Row l={`GST @ ${order.pricing.product_gst_pct}%`} r={fmtINR(order.pricing.product_gst_amount)} />}
            <Row l="Fuel Total" r={fmtINR(order.pricing.product_total ?? order.pricing.subtotal)} />
            <div className="border-t border-dashed border-border pt-1 mt-1" />
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Delivery Invoice (separate)</p>
            <div className="flex justify-between items-center"><span className="text-muted-foreground">Delivery Charge</span>
              {['completed', 'invoice_generated', 'cancelled'].includes(order.status) ? <span>{fmtINR(order.pricing.delivery_charge)}</span> :
                <span className="flex items-center gap-1"><Input value={charge} onChange={(e) => setCharge(e.target.value)} className="h-7 w-24" data-testid="charge-input" /><Button size="sm" variant="ghost" onClick={updateCharge}>Set</Button></span>}
            </div>
            <Row l={`GST @ ${order.pricing.delivery_gst_pct ?? 18}%`} r={fmtINR(order.pricing.delivery_gst_amount ?? order.pricing.gst_amount)} />
            <Row l="Delivery Total" r={fmtINR(order.pricing.delivery_total ?? 0)} />
            <div className="flex justify-between font-semibold border-t border-border pt-1"><span>Grand Total</span><span>{fmtINR(order.pricing.total)}</span></div>
          </div>
          {!['completed', 'invoice_generated', 'cancelled'].includes(order.status) && (
            <div className="border-t border-border pt-2 space-y-2" data-testid="admin-reschedule">
              <Label className="text-xs flex items-center gap-1"><CalendarClock className="h-3.5 w-3.5" />Change delivery slot</Label>
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={reDate} min={new Date().toISOString().slice(0, 10)} onChange={(e) => setReDate(e.target.value)} className="h-8" data-testid="reschedule-date" />
                <Select value={reSlot} onValueChange={setReSlot}>
                  <SelectTrigger className="h-8" data-testid="reschedule-slot"><SelectValue /></SelectTrigger>
                  <SelectContent>{SLOTS.map((s) => <SelectItem key={s.h} value={String(s.h)}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button size="sm" variant="secondary" className="w-full" onClick={reschedule} data-testid="reschedule-button"><Calendar className="h-4 w-4 mr-1" />Update slot &amp; notify customer</Button>
            </div>
          )}
        </div>

        <div className="space-y-4">
          {['driver_assigned', 'en_route', 'arrived', 'delivery_started'].includes(order.status) && <MapView driver={MAP_DRIVER_POS} destination={mapDestination} origin={MAP_ORIGIN_POS} height="200px" />}
          <div className="rounded-xl border border-border bg-card p-4">
            <p className="font-semibold mb-2">Timeline</p>
            <ol className="relative border-l-2 border-border ml-2 space-y-3">
              {(order.timeline || []).map((t, i) => (
                <li key={`${t.status}-${t.at}-${i}`} className="ml-4"><span className="absolute -left-[7px] h-3 w-3 rounded-full bg-primary" /><p className="text-sm font-medium">{t.label}</p>{t.note && <p className="text-xs text-muted-foreground">{t.note}</p>}<p className="text-xs text-muted-foreground">{fmtDateTime(t.at)}</p></li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {order.delivery && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="delivery-proof-card">
          <p className="font-semibold mb-2">Delivery Proof</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Row l="Delivered" r={`${fmtNum(order.delivery.delivered_qty)} L`} />
            <Row l="Meter" r={order.delivery.meter_reading} />
            <Row l="OTP" r={order.delivery.otp_verified ? 'Verified' : 'No'} />
            <Row l="Completed" r={fmtDateTime(order.delivery.completed_at)} />
          </div>
          <div className="flex gap-3 mt-3">
            {order.delivery.photo && <img src={order.delivery.photo} alt="proof" className="h-28 rounded-lg object-cover" />}
            {order.delivery.signature && <img src={order.delivery.signature} alt="sign" className="h-28 rounded-lg border border-border bg-white" />}
          </div>
        </div>
      )}
    </div>
  );
}
const Row = ({ l, r }) => <div className="flex justify-between"><span className="text-muted-foreground">{l}</span><span className="font-medium">{r}</span></div>;
